import React, {
  FC,
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from 'react'

const ScrollMarginContext = createContext<{
  scrollMargin: {
    top: number
    bottom: number
  }
  addTop: (top: number) => () => void
  addBottom: (bottom: number) => () => void
}>({
  scrollMargin: {
    top: 0,
    bottom: 0,
  },
  addTop: () => () => {},
  addBottom: () => () => {},
})

export const ScrollMarginProvider: FC<{ children: any }> = ({ children }) => {
  const [scrollMargin, setScrollMargin] = useState({
    top: 0,
    bottom: 0,
  })

  const addTop = (top = 0) => {
    setScrollMargin((oldMargin) => ({
      ...oldMargin,
      top: oldMargin.top + top,
    }))

    return () => {
      setScrollMargin((oldMargin) => ({
        ...oldMargin,
        top: oldMargin.top - top,
      }))
    }
  }

  const addBottom = (bottom = 0) => {
    setScrollMargin((oldMargin) => ({
      ...oldMargin,
      bottom: oldMargin.bottom + bottom,
    }))
    return () => {
      setScrollMargin((oldMargin) => ({
        ...oldMargin,
        bottom: oldMargin.bottom - bottom,
      }))
    }
  }

  const contextValue = useMemo(
    () => ({ scrollMargin, addTop, addBottom }),
    [scrollMargin]
  )

  return (
    <ScrollMarginContext.Provider value={contextValue}>
      {children}
    </ScrollMarginContext.Provider>
  )
}

export const useScrollMarginValue = () =>
  useContext(ScrollMarginContext).scrollMargin

export const useScrollMargin = ({ top = 0, bottom = 0 }) => {
  const { addTop, addBottom, scrollMargin } = useContext(ScrollMarginContext)

  useEffect(() => {
    const removeTop = addTop(top)
    const removeBottom = addBottom(bottom)
    return () => {
      removeTop()
      removeBottom()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, bottom])

  return scrollMargin
}
